import React, { useRef } from "react";
import useAddUserDocument from "../../hooks/useAddUserDocument";
import useAddNotification from "../../hooks/useAddNotification";
import { GET_USER } from "../../hooks/useGetUser";
import useTranslations from "../../hooks/useTranslations";
import { PlusIcon } from "../icons";
import { Button, H4 } from "../wombat-ui";
import { DocumentPlate } from "../utils/DocumentPlate";
import useDeleteUserDocument from "../../hooks/useDeleteUserDocument";
import { UserMadFragment } from "../../graphql/fragments";

type UserDocumentsSectionProps = {
  user: UserMadFragment;
  vertical: boolean;
};

const UserDocumentsSection: React.FC<UserDocumentsSectionProps> = ({
  user,
  vertical,
}) => {
  const t = useTranslations();

  const fileUploadField = useRef<HTMLInputElement>();

  const [
    addUserDocument,
    { loading: addUserDocumentLoading },
  ] = useAddUserDocument({
    refetchQueries: [{ query: GET_USER, variables: { id: user.id } }],
  });
  const [addNotification] = useAddNotification();

  const [deleteUserDocument, { loading }] = useDeleteUserDocument({
    refetchQueries: [{ query: GET_USER, variables: { id: user.id } }],
  });

  const handleUploadDocument = (e) => {
    const reader = new FileReader();
    const file = e.target.files[0];
    reader.readAsDataURL(file);
    reader.onload = () => {
      addUserDocument({
        variables: {
          input: {
            id: user.id,
            data: reader.result,
            filename: file.name,
          },
        },
      }).catch(() => {
        addNotification({
          variables: {
            title: t("forms.errors.notificationTitle") as string,
            body: t("forms.errors.invalidFile") as string,
            status: "error",
          },
        });
      });
    };
  };

  if (!user) {
    return null;
  }

  return (
    <div className={vertical ? undefined : "flex items-center space-x-6 pb-6"}>
      <div
        className={
          vertical
            ? "flex items-end justify-between pt-4"
            : "flex items-center space-x-6"
        }
      >
        <div>
          <H4>{t("collaborator.documents")}</H4>
        </div>
        {user.canAddDocument && (
          <div>
            <input
              ref={fileUploadField}
              type="file"
              onChange={handleUploadDocument}
              name="document"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                margin: 0,
                padding: 0,
                opacity: 0,
                width: 1,
                height: 1,
              }}
            />
            <Button
              disabled={addUserDocumentLoading}
              variant="subtle"
              loading={addUserDocumentLoading}
              id="addDocument"
              onClick={() => fileUploadField?.current?.click()}
              icon={PlusIcon}
              scale="xs"
            />
          </div>
        )}
      </div>
      <div>
        {user.documents.length === 0 ? (
          <div className="pt-1 text-sm text-light-900">
            <p>{t("collaborator.noDocuments")}</p>
          </div>
        ) : (
          <ul
            id="documentPlates"
            className={
              vertical
                ? undefined
                : "grid gap-4 lg:grid-cols-4 xs:grid-cols-1 md:grid-cols-2"
            }
          >
            {user.documents.map((document) => (
              <DocumentPlate
                key={document.id}
                allowDelete={user.canAddDocument}
                document={document}
                deleteDocument={deleteUserDocument}
                deleteLoading={loading}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserDocumentsSection;
